import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, of } from 'rxjs';
import { Job, JobPayload } from '../../model/tops.models';
import { AuditAction } from '../../model/audit.models';
import { parseAppointment } from '../../mixins/date-utils';
import { ApiService } from '../../service/api.service';
import { AuditService } from '../../service/audit.service';
import { ToastService } from '../../service/toast.service';

@Component({
  selector: 'app-job-form-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './job-form-modal.component.html',
  styleUrl: './job-form-modal.component.css',
})
export class JobFormModalComponent implements OnChanges {
  @Input() open = false;
  @Input() job: Job | null = null;
  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<Job>();
  @Output() prefill = new EventEmitter<Record<string, unknown>>();

  private readonly fb = inject(FormBuilder);
  private readonly api = inject(ApiService);
  private readonly audit = inject(AuditService);
  private readonly toast = inject(ToastService);

  submitting = false;
  fetchingRemote = false;

  readonly form = this.fb.nonNullable.group({
    jobNumber: [''],
    address: ['', Validators.required],
    appointment: [''],
    status: ['In Progress'],
    reportType: [''],
  });

  /** Control names only - values would put property data into the audit trail. */
  private invalidControlNames(): string[] {
    return Object.entries(this.form.controls)
      .filter(([, control]) => control.invalid)
      .map(([name]) => name);
  }

  ngOnChanges(): void {
    if (!this.open) return;
    this.form.reset({
      jobNumber: this.job?.jobNumber || '',
      address: this.job?.address || '',
      appointment: this.buildAppointment(this.job),
      status: this.job?.status || 'In Progress',
      reportType: this.job?.reportType || '',
    });
    if (this.job) {
      this.form.controls.reportType.disable({ emitEvent: false });
    } else {
      this.form.controls.reportType.enable({ emitEvent: false });
    }
  }

  lookupRemoteJob(): void {
    const jobNumber = this.form.controls.jobNumber.value.trim();
    if (!jobNumber || !navigator.onLine) return;
    this.fetchingRemote = true;
    this.api.getRemoteJobDetails(jobNumber)
      .pipe(catchError(() => of(null)))
      .subscribe((details) => {
        this.fetchingRemote = false;
        if (!details) {
          this.toast.error('Remote lookup failed', `Job ${jobNumber} was not found or the remote service is offline.`);
          return;
        }
        this.form.patchValue({
          address: details.job.address,
          reportType: details.job.reportType || '',
          appointment: this.buildAppointment(details.job),
        });
        this.prefill.emit(details.propertyInfo);
        this.toast.success('Job loaded', `Details for job ${jobNumber} have been pre-filled.`);
      });
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.audit.track({
        logSource: 'OFFLINE',
        category: 'VALIDATION',
        action: AuditAction.VALIDATION_FAILED,
        severity: 'WARNING',
        outcome: 'FAILURE',
        entityType: 'Job',
        message: 'Job form validation failed.',
        detail: { invalidControls: this.invalidControlNames() },
      });
      return;
    }

    this.audit.track({
      logSource: 'OFFLINE',
      category: 'VALIDATION',
      action: AuditAction.VALIDATION_COMPLETED,
      entityType: 'Job',
      itemCount: 1,
      message: 'Job form validation passed.',
    });
    const { appointmentDate, appointmentTime } = parseAppointment(this.form.controls.appointment.value);
    const payload: JobPayload = {
      jobNumber: this.form.controls.jobNumber.value.trim() || null,
      address: this.form.controls.address.value.trim(),
      appointmentDate,
      appointmentTime,
      status: this.form.controls.status.value,
      reportType: this.form.controls.reportType.value.trim() || null,
    };
    this.submitting = true;
    const request = this.job ? this.api.updateJob(this.job.id, payload) : this.api.createJob(payload);
    request.subscribe({
      next: (savedJob) => {
        this.submitting = false;
        this.toast.success('Success', this.job ? 'Job updated successfully.' : 'Job created successfully.');
        this.saved.emit(savedJob);
      },
      error: (error) => {
        this.submitting = false;
        this.toast.error('Error', error?.error?.message || error?.message || 'Failed to save job.');
      },
    });
  }

  setAppointmentNow(): void {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    this.form.controls.appointment.setValue(`${day}-${month}-${year} ${hours}:${minutes}`);
  }

  private buildAppointment(job: Job | JobPayload | null): string {
    if (!job?.appointmentDate) return '';
    const value = String(job.appointmentDate).replace(/\//g, '-');
    const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    const displayDate = iso ? `${iso[3]}-${iso[2]}-${iso[1]}` : value.slice(0, 10);
    return job.appointmentTime ? `${displayDate} ${job.appointmentTime}` : displayDate;
  }

}
